"use client";
import { z } from "zod";
import { phoneNumberValidation } from "@/app/model/login";
import { Input } from "@/stories/input/input";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import React, { useEffect, useRef, useState } from "react";
import Info from "@/app/assets/icons/info";
import LoadingIcon from "@/app/assets/icons/loading";
import ConsentDisclaimer from "@/app/components/shared/consent-disclaimer";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { getPartnerList } from "@/app/api/service/partner";
import { getXPartner, isMTDomain, setXPartner } from "@/app/utils/helper";
import { ICM_NAME_FULL } from "@/app/utils/constants";

interface PhoneInputProps {
    phoneNumber: string;
    setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
    handlePhoneVerify: (type: string) => void;
    buttonLoader: boolean;
    otpErrorMessage?: string | null;
    setOtpErrorMessage?: (type: string) => void;
    enableAutoSubmit?: boolean; // New flag to control auto-submit feature
}

const PARTNER_MT = {
    DEFAULT: "" /* "oro" */,
};
const SUB_PARTNER_MT = {
    DEFAULT: "Empty selection" /* "icm1" */,
};
const PSP_LIST = 2000;

const PhoneInput: React.FC<PhoneInputProps> = ({
    phoneNumber,
    setPhoneNumber,
    handlePhoneVerify,
    buttonLoader,
    otpErrorMessage,
    setOtpErrorMessage,
    enableAutoSubmit = false // Default to false (auto-submit disabled)

}) => {
    const [error, setError] = useState<string | null>(null);
    const [hasBtnDisabled, setHasBtnDisabled] = useState<boolean>(true);
    const [hasBeenClicked, setHasBeenClicked] = useState<boolean>(false); // Track if button was clicked
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [partner, setPartner] = useState(getXPartner());
    const [partnerList, setPartnerList] = useState([] /* ["1", "2", "3", "4", "5"] */ );
    const [subPartner, setSubPartner] = useState(/* SUB_PARTNER_MT.DEFAULT */ "");
    const [subPartnerList /* setSubPartnerList */] = useState(
        /* [SUB_PARTNER_MT.DEFAULT, "icm2"] */ [
            "Empty selection",
            ...new Array(PSP_LIST).fill("").map((item, index) => {
                return ++index;
            }),
        ],
    );

    useEffect(() => {
        if (process.env.SHOW_PSP === "true") {
            if (partner === "" || partner === "undefined" || partner === "null") {
                setPartner(PARTNER_MT.DEFAULT);
                setXPartner(PARTNER_MT.DEFAULT, "");
            }
    
            if (isMTDomain()) {
                const fetchPartnerList = async () => {
                    const partnerList = await getPartnerList();
                    if (Object.entries(partnerList).length > 0) {
                        const arr_50 = new Array(50).fill(""); // create an array of 50 empty strings of Partners.
                        arr_50.forEach((element, index) => {
                            if (partnerList[index] == undefined) {
                                partnerList.push(index + 1);
                            }
                        });
                        // console.log("partnerList >>>>", partnerList);
                        setPartnerList(partnerList);
                    }
                };
                fetchPartnerList();
            }
        }
    }, []);

    useEffect(() => {
        if (!enableAutoSubmit || hasBeenClicked) return; // Exit early if auto-submit is disabled or button already clicked

        if (phoneNumber.trim().length === 10) {
            const timer = setTimeout(() => {
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }

                handleVerify();
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [phoneNumber, enableAutoSubmit, hasBeenClicked]);

    const validatePhoneNumber = (value: string) => {
        try {
            const cleanedValue = value.trim(); // Ensure it trims whitespace

            phoneNumberValidation.parse(cleanedValue); // Validate using Zod schema

            return true;
        } catch (err) {
            if (err instanceof z.ZodError) {
                setError(err.errors[0].message); // Display the first validation error
            }

            return false;
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, ""); // allow only digits

        if (value.length > 10) {
            value = value.slice(0, 10); // limit to 10 digits
        }

        // ✅ If phone number changes after button click, re-enable button
        if (hasBeenClicked) {
            setHasBeenClicked(false);
        }

        if (value.trim().length > 0) {
            setHasBtnDisabled(false);
        } else {
            setHasBtnDisabled(true);
        }

        setPhoneNumber(value);

        // Clear error as soon as the user starts editing
        if (error) {
            setError(null);
            if (setOtpErrorMessage) {
                setOtpErrorMessage("");
            }
        }
    };

    const handleVerify = () => {
        setHasBeenClicked(true);

        setTimeout(() => {
           setHasBeenClicked(false);
       }, 30000);

        if (isMTDomain() && !partner) {
            setXPartner(PARTNER_MT.DEFAULT, SUB_PARTNER_MT.DEFAULT);
        }
        // Validate the current phone number on button click
        const isValid = validatePhoneNumber(phoneNumber);

        // Only proceed with verification if validation passes
        if (isValid) {
            setError(null); // Clear any lingering errors
            handlePhoneVerify("phone");
        }
    };

    const isIOS = typeof navigator !== "undefined" && /iP(hone|od|ad)/.test(navigator.userAgent);

    return (
        <div className="flex w-full flex-col justify-start">
            <h1 className="mb-1 mt-1 text-lg font-regular leading-[32.8px] tracking-[-0.2px] text-black md:mt-0 md:text-sm md:font-semibold md:leading-[16.8px] md:tracking-[0.7px]">
                Welcome to {ICM_NAME_FULL}!
            </h1>
            <p className="mb-1 text-3xl font-semibold leading-[30px] tracking-[-0.48px] md:mb-8">
                Let&#39;s start with your mobile number
            </p>
            <div
                className={`mb-0 flex h-[66px] w-full items-center justify-start gap-1 rounded-[12px] border p-s7 transition-colors duration-500 ${error ? "border-error-600" : "border-gray-200"}`}
            >
                {phoneNumber && phoneNumber.length && <p className="mr-1 md:text-lg">+91</p>}
                <Input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="+91 00000 00000"
                    value={phoneNumber}
                    ref={inputRef}
                    onChange={handleInputChange}
                    onKeyUp={(e) => {
                        if (setOtpErrorMessage) setOtpErrorMessage("");
                        if (
                            !hasBtnDisabled &&
                            !hasBeenClicked &&
                            String(e.key)?.toLowerCase() === "enter"
                        ) {
                            handleVerify();
                        }
                    }}
                    showIcon={false}
                    className="mx-0 h-14 border-b-0 !px-0 md:text-lg"
                    style={{
                        ...(isIOS ? { fontSize: "16px", lineHeight: "1.2" } : {}),
                      }}
                />
            </div>
            <div
                className={`overflow-hidden transition-all duration-500 ${
                    otpErrorMessage ? "max-h-[60px] h-[60px] opacity-100" : "mb-0 mt-0 max-h-0 opacity-0"
                }`}
            >
                <div className="flex items-start gap-2 md:mt-3 mt-2 h-[60px]">
                    <Info
                        width="12"
                        height="12"
                        className="text-error-600 transition-all duration-500 mt-1"
                    />
                    <p className="text-sm text-error-600 transition-all duration-500">
                        {otpErrorMessage}
                    </p>
                </div>
            </div>
            <div
                className={`flex transform items-start gap-2 transition-all duration-500 ${
                    error
                        ? "mb-2 mt-2 translate-y-0 opacity-100"
                        : "mb-0 mt-0 -translate-y-2 opacity-0"
                }`}
            >
                <Info width="12" height="12" className="text-error-600 mt-1" />
                <p className="text-sm text-error-600">{error}</p>
            </div>

            <div className="flex flex-col-reverse gap-2 md:flex-col md:gap-0 md:mb-0">
                <RegularButton
                    size={ButtonSize.Large}
                    variant={ButtonVariant.PrimaryDefault}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full focus-visible-active:ring-2 focus-visible-active:ring-neutralOpacityLight-8 font-medium ring-offset-background focus-visible:outline-none active:ring-2 active:ring-neutralOpacityDark-8 focus-visible:ring-2 focus-visible:ring-black disabled:pointer-events-none bg-brand-primary-500 text-white hover:bg-brand-primary-600 focus:bg-brand-primary-500 disabled:bg-neutralOpacityDark-8 disabled:text-neutralOpacityDark-30 py-s7 px-s9 gap-s6 max-w-fit mt-0 w-full text-lg transition-opacity duration-300 ease-in-out md:w-full md:relative md:bottom-auto mb-4 md:mb-0 md:mt-4 min-w-full"
                    noIcon={true}
                    label={
                        buttonLoader ? (
                            <LoadingIcon className="m-auto h-5 w-5 animate-spin" />
                        ) : (
                            "Get OTP"
                        )
                    }
                    showRightIcon={false}
                    disabled={
                        buttonLoader || hasBtnDisabled || hasBeenClicked || phoneNumber?.length < 10
                    }
                    onClick={handleVerify}
                />
            </div>

            {
                /* false && */ process.env.SHOW_PSP === "true" &&
                    partnerList.length > 0 &&
                    isMTDomain() && (
                        <>
                            <Select
                                value={partner}
                                onValueChange={(value) => {
                                    setPartner(value);
                                    setXPartner(value, subPartner);
                                }}
                            >
                                <SelectTrigger className="-mb-2 mt-6 h-16 w-full rounded-[12px] text-sm placeholder:font-regular placeholder:leading-[140%] placeholder:text-neutral-300 md:text-lg [&[data-placeholder]>span]:text-neutral-300">
                                    <SelectValue placeholder="Select Partner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {partnerList.map((name, index) => (
                                        <SelectItem
                                            key={name}
                                            className="h-16 text-sm md:text-lg"
                                            value={String(index + 1)}
                                        >
                                            {name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                        <Select
                            value={subPartner}
                            onValueChange={(value) => {
                                setSubPartner(value);
                                setXPartner(partner, value);
                            }}
                            disabled={!partner ? true : false}
                        >
                            <SelectTrigger className="-mb-2 mt-6 h-16 w-full rounded-[12px] text-sm placeholder:font-regular placeholder:leading-[140%] placeholder:text-neutral-300 md:text-lg [&[data-placeholder]>span]:text-neutral-300">
                                <SelectValue placeholder="Select Partner" />
                            </SelectTrigger>
                            <SelectContent>
                                {subPartnerList.map((name, index) => {
                                    return (
                                        <SelectItem
                                            key={index}
                                            className="h-16 text-sm md:text-lg"
                                            value={name ? String(name) : String(index + 1)}
                                        >
                                            {name ? name : String(index + 1)}
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </>
                )
            }

            <ConsentDisclaimer type={"phone"} classnames="md:mt-8 mt-0" />
        </div>
    );
};

export default PhoneInput;
